import { z } from 'zod';
import {
  chatMessageSchema,
  facingSchema,
  inviteCodeSchema,
  memberSchema,
  memberTokenSchema,
  messageIdSchema,
  reactionEmojiSchema,
  reactionSchema,
  roomInfoSchema,
  seqSchema,
  timestampSchema,
  userIdSchema,
} from './primitives.js';

/**
 * 서버 → 클라이언트 프레임. 판별자는 `t`.
 */

/** hello에 대한 응답. 방 상태 전체를 한 번에 준다. */
export const welcomeSchema = z.object({
  t: z.literal('welcome'),
  v: z.number().int().positive(),
  room: roomInfoSchema,
  /** 이 연결의 신원. */
  you: memberSchema,
  /** 저장해 두었다가 다음 접속의 hello에 실어 보낸다. */
  memberToken: memberTokenSchema,
  members: z.array(memberSchema),
  /** sinceSeq를 줬으면 그 이후분, 아니면 최근 페이지. */
  messages: z.array(chatMessageSchema),
  /**
   * messages에 달린 리액션.
   *
   * 리액션에는 seq가 없어서 델타 동기화로 따라올 수 없다. 그래서 재접속
   * 여부와 무관하게 최근 메시지 구간의 리액션을 통째로 다시 보낸다.
   */
  reactions: z.array(reactionSchema),
  /** 방의 현재 최대 seq. messages가 잘렸는지 판단할 수 있다. */
  latestSeq: seqSchema,
  /** 클라이언트 시계 보정용. */
  serverTime: timestampSchema,
});

export const messageSchema = z.object({
  t: z.literal('message'),
  message: chatMessageSchema,
});

export const messageUpdatedSchema = z.object({
  t: z.literal('messageUpdated'),
  id: messageIdSchema,
  body: z.string(),
  editedAt: timestampSchema,
});

export const messageDeletedSchema = z.object({
  t: z.literal('messageDeleted'),
  id: messageIdSchema,
  deletedAt: timestampSchema,
});

export const reactionUpdateSchema = z.object({
  t: z.literal('reaction'),
  msgId: messageIdSchema,
  userId: userIdSchema,
  emoji: reactionEmojiSchema,
  on: z.boolean(),
});

/**
 * 프레즌스 배치.
 *
 * 아바타 좌표는 초당 수십 번 갱신되므로 개별 전송하지 않고
 * PRESENCE_TICK_MS 주기로 묶어 보낸다.
 */
export const presenceSchema = z.object({
  t: z.literal('presence'),
  updates: z.array(
    z.object({
      userId: userIdSchema,
      x: z.number(),
      y: z.number(),
      facing: facingSchema,
    }),
  ),
});

export const memberJoinedSchema = z.object({
  t: z.literal('memberJoined'),
  member: memberSchema,
});

export const memberLeftSchema = z.object({
  t: z.literal('memberLeft'),
  userId: userIdSchema,
});

export const memberUpdatedSchema = z.object({
  t: z.literal('memberUpdated'),
  member: memberSchema,
});

export const typingUpdateSchema = z.object({
  t: z.literal('typing'),
  userId: userIdSchema,
  on: z.boolean(),
});

export const interactUpdateSchema = z.object({
  t: z.literal('interact'),
  userId: userIdSchema,
  objectId: z.string(),
});

export const errorSchema = z.object({
  t: z.literal('error'),
  code: z.string(),
  message: z.string(),
  /** 어떤 클라이언트 프레임 때문인지. 없으면 연결 수준 오류. */
  ref: z.string().optional(),
});

export const pongSchema = z.object({
  t: z.literal('pong'),
  serverTime: timestampSchema,
});

/** 방 설정이 바뀌었다. 이름·잠금 상태 등. */
export const roomUpdatedSchema = z.object({
  t: z.literal('roomUpdated'),
  room: roomInfoSchema,
});

/**
 * 초대코드가 새로 발급됐다. 구코드는 이미 죽었다.
 *
 * 붙어 있는 사람은 끊기지 않는다 — 소켓은 이미 인증을 마쳤다. 다만 저장해
 * 둔 재접속 토큰과 '마지막 방'이 구코드로 묶여 있으므로, 받는 즉시
 * 새 코드로 갈아끼워야 한다. 안 그러면 다음 재접속이 404로 죽는다.
 */
export const codeRotatedSchema = z.object({
  t: z.literal('codeRotated'),
  code: inviteCodeSchema,
});

export const serverMessageSchema = z.discriminatedUnion('t', [
  welcomeSchema,
  messageSchema,
  messageUpdatedSchema,
  messageDeletedSchema,
  reactionUpdateSchema,
  presenceSchema,
  memberJoinedSchema,
  memberLeftSchema,
  memberUpdatedSchema,
  typingUpdateSchema,
  interactUpdateSchema,
  errorSchema,
  pongSchema,
  roomUpdatedSchema,
  codeRotatedSchema,
]);

export type ServerMessage = z.infer<typeof serverMessageSchema>;
export type ServerMessageType = ServerMessage['t'];

export type WelcomeMessage = z.infer<typeof welcomeSchema>;
export type PresenceMessage = z.infer<typeof presenceSchema>;
export type ErrorMessage = z.infer<typeof errorSchema>;
